import type { ChangeEvent, FormEvent } from 'react';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { generatePath } from 'react-router-dom';
import type {
  GridColumnHeaderParams,
  GridPaginationModel,
  GridRowId,
  GridRowSelectionModel,
  MuiEvent,
} from '@mui/x-data-grid';
import { GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid';
import { difference, isEmpty } from 'lodash';

import { FILTER_KEY } from 'src/constants/keySession';
import { usePrevious } from 'src/hooks';
import type { UserAdminRequestState } from 'src/types/user';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpServiceInstance from 'src/services/api/http';
import { routePaths } from 'src/services/routing/routePaths';
import { useCustomizing } from 'src/components/app/context/CustomizingContext';
import { useFilterContext } from 'src/components/app/context/FilterContext';
import { useDownloadManager } from 'src/components/downloadManager/DownloadManagerContext';
import { useUserAdmin } from 'src/components/userAdmin/UserAdminContext';
import type { UserAdminDetailHistoryState } from 'src/components/userAdmin/UserAdminDetailContext';
import { filterSpecs } from 'src/components/userAdmin/UserListPage/filterTree';

/**
 * Returns a new selection model with the given page's selection replaced.
 * Pure on purpose: state updaters must not mutate the previous state.
 */
export const updatePagedSelectionModel = (
  previousModel: Record<number, GridRowId[]>,
  page: number,
  selection: GridRowId[],
): Record<number, GridRowId[]> => ({
  ...previousModel,
  [page]: selection,
});

export const useUserList = () => {
  const {
    isFullSelection,
    searchResultsCount,
    page,
    setIsFullSelection,
    setSearchString,
    searchResults,
  } = useUserAdmin();
  const { submitSearch, setPageNumber } = useUserAdmin();
  const history = useHistory();
  const { currentApp } = useCustomizing();
  const { poll } = useDownloadManager();

  const [downloading, setDownLoading] = useState<
    UserAdminRequestState | undefined
  >(undefined);
  const { filterGroups, setFilterGroups, reset } = useFilterContext();
  const previousFilterGroups = usePrevious(filterGroups);
  const hideUserAdminDownload = !!currentApp?.hide_user_admin_download;

  // custom selection model, that remembers selections across pages
  const [pagedSelectionModel, setPagedSelectionModel] = useState<
    Record<number, GridRowId[]>
  >({});

  // flatten all pages, as runtime requires {type, ids} format despite
  // the TypeScript definition.
  const selectionModelArray = Object.values(pagedSelectionModel).flat();
  const selectionModel: GridRowSelectionModel = {
    type: 'include' as const,
    ids: new Set(selectionModelArray),
  };

  const selectionCount = isFullSelection
    ? searchResultsCount
    : selectionModelArray.length;

  const handleSelectionModelChange = (model: GridRowSelectionModel) => {
    /*
    Unlike its predecessor, MUI x-data-grid v8 uses an inverted selection model:
    - {type: 'include', ids: Set([1,2,3])} means "select these specific rows"
    - {type: 'exclude', ids: Set([])} means "select all rows EXCEPT none" = select all

    When the header checkbox is clicked to select all, v8 passes {type: 'exclude', ids: Set([])}.
    This detects the exclude-with-empty-set case and converts it to all current page row IDs:
     */
    let modelIds: GridRowId[];

    if (model.type === 'exclude' && model.ids.size === 0) {
      // "exclude none" = select all:
      modelIds = searchResults.map((row) => row.id);
    } else {
      // "include specific IDs" / "exclude specific IDs" = deselect all:
      modelIds = Array.from(model.ids);
    }

    // since the selection model always contains all rows from all pages, the selection model of the
    // current page can be computed by taking the difference between the selection model of
    // all other pages and the current model
    const otherPagesModel = Object.entries(pagedSelectionModel)
      .filter(([_page]) => parseInt(_page) !== page)
      .map(([, model]) => model)
      .flat();
    const currentPageSelectionModel = difference(modelIds, otherPagesModel);

    if (isFullSelection) {
      // if we previously were in the full-selection mode, reset the selection to only contain
      // the current page
      setPagedSelectionModel({
        [page]: currentPageSelectionModel,
      });
    } else {
      // otherwise, update only the current page
      setPagedSelectionModel((previousModel) =>
        updatePagedSelectionModel(
          previousModel,
          page,
          currentPageSelectionModel,
        ),
      );
    }

    // any time the selection model changes (which is also the case, when we navigate between pages),
    // we reset the full-selection flag
    setIsFullSelection(false);
  };

  /**
   * Handle column header click.
   */
  const handleColumnHeaderClick = (
    params: GridColumnHeaderParams,
    event: MuiEvent<React.MouseEvent>,
  ) => {
    // Reset the full-selection mode, when clearing the checkbox in the header column.
    if (
      isFullSelection &&
      params.field === GRID_CHECKBOX_SELECTION_COL_DEF.field
    ) {
      event.preventDefault();
      setIsFullSelection(false);
      setPagedSelectionModel({});
    }
  };

  /**
   * Go to detail and save the current filterGroups in sessionStorage.
   */
  const goToDetail = (id: number, state?: UserAdminDetailHistoryState) => {
    const detailPath = generatePath(routePaths.USER_ADMIN_DETAIL, {
      id,
    });

    if (!isEmpty(filterGroups)) {
      // We need to set filters in session as the FilterContext is not globally used
      // find more information about this in comment above UserListPageWrapper.
      sessionStorage.setItem(FILTER_KEY, JSON.stringify(filterGroups));
    }

    history.push(detailPath, state);
  };

  const handleDownloadClick = useCallback(() => {
    setDownLoading({ status: 'pending' });

    HttpServiceInstance.get(apiPaths.ADMIN.USER_EXPORT, {}, true)
      .finally(() => {
        setDownLoading({ status: 'success' });
        poll();
      })
      .catch(() => setDownLoading({ status: 'error' }));
  }, [poll]);

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitSearch();
  };

  const handleSearchStringChange = (event: ChangeEvent<HTMLInputElement>) =>
    setSearchString(event.target.value);

  /**
   * Handle page change.
   * The DataGrid component uses a zero-based index, the API uses a one-based index.
   */
  const handlePageChange = ({ page }: GridPaginationModel) => {
    setPageNumber(page + 1);
  };

  // Reset all previously selected rows across all pages and only
  // select all rows on the current page when `isFullSelection` was set to `true`.
  useEffect(() => {
    if (isFullSelection) {
      setPagedSelectionModel({
        [page]: searchResults.map((user) => user.id),
      });
    }
  }, [isFullSelection]);

  // Perform search when the filter context's filterGroups changed.
  useEffect(() => {
    if (
      !!previousFilterGroups &&
      !!filterGroups &&
      previousFilterGroups !== filterGroups
    ) {
      setPagedSelectionModel({});
      setIsFullSelection(false);
    }
  }, [filterGroups]);

  // If filterSettings are stored in sessionStorage set them
  // else reset filterContext on mount.
  useEffect(() => {
    const filterSetting = sessionStorage.getItem(FILTER_KEY);

    if (filterSetting) {
      setFilterGroups(JSON.parse(filterSetting));
    } else {
      reset({ defaultFilterGroups: {}, filterSpecs });
    }
  }, []);

  return {
    goToDetail,
    handleSearchSubmit,
    handleSearchStringChange,
    selectionCount,
    hideUserAdminDownload,
    downloading,
    handleDownloadClick,
    handleColumnHeaderClick,
    handlePageChange,
    handleSelectionModelChange,
    selectionModel,
  };
};
